import { useEffect, useRef, useState } from "react";
import { api, API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Folder, FolderPlus, Upload, Trash2, Download } from "lucide-react";

export default function DownloadsTab() {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [opened, setOpened] = useState(null);
  const [files, setFiles] = useState([]);
  const [newFolder, setNewFolder] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const isAdmin = user?.role === "admin";

  const loadFolders = async () => {
    const { data } = await api.get(`/downloads/folders`);
    setFolders(data.items || []);
  };
  const loadFiles = async (f) => {
    const { data } = await api.get(`/downloads/folders/${f.id}/files`);
    setFiles(data.items || []);
  };
  useEffect(() => { loadFolders(); }, []);

  const createFolder = async () => {
    if (!newFolder.trim()) return;
    await api.post(`/admin/downloads/folders`, { name: newFolder.trim() });
    setNewFolder(""); await loadFolders();
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"?`)) return;
    await api.delete(`/admin/downloads/folders/${f.id}`);
    setOpened(null); setFiles([]); await loadFolders();
  };
  const upload = async (file) => {
    if (!file || !opened) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/downloads/folders/${opened.id}/upload`, fd, {
        params: { description: "" },
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadFiles(opened);
      await loadFolders();
    } finally { setUploading(false); }
  };
  const downloadFile = async (f) => {
    const token = localStorage.getItem("auth_token");
    const url = `${API}/downloads/files/${f.id}/download${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
    window.open(url, "_blank");
  };
  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.name}"?`)) return;
    await api.delete(`/admin/downloads/files/${f.id}`);
    await loadFiles(opened);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5" data-testid="downloads-tab">
      <aside className="csc-card p-4 space-y-3">
        <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">Folders</h3>
        {isAdmin && (
          <div className="flex gap-2">
            <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="New folder name…" className="csc-input !py-2" data-testid="new-folder-input" />
            <button onClick={createFolder} className="csc-btn-primary !py-2 !px-3" data-testid="new-folder-btn"><FolderPlus size={13} /></button>
          </div>
        )}
        <ul className="space-y-1">
          {folders.length === 0 && <li className="text-xs text-[#94a3b8] font-mono-tech uppercase tracking-wider">No folders.</li>}
          {folders.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2">
              <button onClick={() => { setOpened(f); loadFiles(f); }} className={`flex-1 flex items-center gap-2 text-sm text-left px-2 py-1.5 rounded ${opened?.id === f.id ? "bg-[#00aaff]/10 text-[#00aaff]" : "text-[#94a3b8] hover:text-white"}`} data-testid={`folder-${f.id}`}>
                <Folder size={13} /> <span className="truncate">{f.name}</span>
                <span className="ml-auto font-mono-tech text-[10px]">{f.files_count}</span>
              </button>
              {isAdmin && <button onClick={() => deleteFolder(f)} className="text-[#94a3b8] hover:text-red-400" title="Delete folder"><Trash2 size={12} /></button>}
            </li>
          ))}
        </ul>
      </aside>

      <section className="csc-card p-5">
        {!opened ? (
          <div className="text-center text-[#94a3b8] py-10" data-testid="downloads-empty">Select or create a folder to begin.</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold">{opened.name}</h3>
              {isAdmin && (
                <>
                  <button onClick={() => fileInput.current?.click()} disabled={uploading} className="csc-btn-primary !py-2" data-testid="upload-file-btn">
                    <Upload size={13} /> {uploading ? "Uploading…" : "Upload file"}
                  </button>
                  <input ref={fileInput} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                </>
              )}
            </div>
            {opened.description && <p className="text-xs text-[#94a3b8] mt-1">{opened.description}</p>}
            <table className="w-full text-sm mt-4">
              <thead className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]">
                <tr><th className="text-left py-2">File</th><th className="text-right py-2">Size</th><th className="text-right py-2">Downloads</th><th className="text-right py-2">Uploaded</th><th></th></tr>
              </thead>
              <tbody>
                {files.length === 0 && <tr><td colSpan={5} className="text-center text-[#94a3b8] py-6 font-mono-tech text-xs uppercase tracking-wider">Empty folder.</td></tr>}
                {files.map((f) => (
                  <tr key={f.id} className="border-t border-[rgba(0,170,255,0.18)]" data-testid={`download-file-${f.id}`}>
                    <td className="py-2 text-white">{f.name}</td>
                    <td className="py-2 text-right font-mono-tech text-[11px] text-[#94a3b8]">{(f.size/1024).toFixed(0)} KB</td>
                    <td className="py-2 text-right font-mono-tech text-[11px] text-[#94a3b8]">{f.download_count}</td>
                    <td className="py-2 text-right font-mono-tech text-[11px] text-[#94a3b8]">{new Date(f.created_at).toLocaleDateString()}</td>
                    <td className="py-2 text-right">
                      <button onClick={() => downloadFile(f)} className="csc-btn-ghost !py-1.5 !px-2" data-testid={`dl-${f.id}`}><Download size={12} /></button>
                      {isAdmin && <button onClick={() => deleteFile(f)} className="csc-btn-ghost !py-1.5 !px-2 ml-1 hover:!border-red-400 hover:!text-red-400"><Trash2 size={12} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </div>
  );
}
